import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import privateAxios from "../../utils/axiosPrivate";
import {
  getPartnerDashboardURL,
  getPartnerCommissionURL
} from "../../utils/constants/apiEndPoints";
import { PARTNER_DASHBOARD } from "../../route/paths";
import PartnerPortalHeader from "./PartnerPortalHeader";
import ReferralStrip from "./ReferralStrip";
import AddCustomerModal from "./AddCustomerModal";
import { FT, fmt } from "./partnerPortalShared";

export default function PartnerCommission() {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  // Org-lead drill-down: null = whole organisation, otherwise a single sub-partner id.
  const [partnerFilter, setPartnerFilter] = useState(null);

  const withPartner = (url) =>
    partnerFilter ? `${url}?partnerId=${partnerFilter}` : url;

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [dashRes, summaryRes] = await Promise.all([
          privateAxios.get(withPartner(getPartnerDashboardURL)),
          privateAxios.get(withPartner(getPartnerCommissionURL))
        ]);
        setDashboard(dashRes.data?.data || null);
        setSummary(summaryRes.data?.data || null);
      } catch (e) {
        setError(
          e?.response?.data?.message ||
            "Couldn't load commission history. Please try again."
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [partnerFilter]);

  const isLead = !!dashboard?.organisationLead;
  const members = dashboard?.members || [];
  // Head-contact "lump sum split" view: when a lead is looking at the whole org,
  // group each month by partner with a per-partner subtotal so they know exactly
  // how much to forward to each sub-partner. A single-partner filter falls back to
  // the normal flat payout list.
  const showDistribution = isLead && !partnerFilter;

  const paid = summary?.paid || [];
  const totalPaid = summary?.totalPaid ?? 0;
  const lifetimeInvoices = summary?.lifetimeInvoices ?? 0;
  const averagePayout = summary?.averagePayout ?? 0;
  // Only treat genuinely future payouts as "upcoming". A projected payout date
  // that has already passed (e.g. an unrecorded month) must not read as future.
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const upcoming = (summary?.upcoming || []).filter(
    (p) => p.paidOn && new Date(p.paidOn) >= startOfToday
  );
  const expectedUpcoming = upcoming.reduce((s, p) => s + (p.amount || 0), 0);

  let signupDisplayUrl;
  if (dashboard?.signupPageUrl) {
    signupDisplayUrl = dashboard.signupPageUrl.replace(/^https?:\/\//, "");
  } else if (dashboard?.referralCode) {
    signupDisplayUrl = `fundtap.co/partners/${dashboard.referralCode.toLowerCase()}`;
  } else {
    signupDisplayUrl = "fundtap.co/partners/your-code";
  }

  return (
    <div
      style={{
        width: "100%",
        minHeight: "100vh",
        background: FT.paperWarm,
        color: FT.navy,
        fontFamily: "DM Sans, system-ui, sans-serif"
      }}
    >
      <PartnerPortalHeader
        partnerName={dashboard?.partnerName || ""}
        referralCode={dashboard?.referralCode}
        signupPageUrl={dashboard?.signupPageUrl}
        isLead={isLead}
        onAddClient={() => setAddOpen(true)}
      />

      {loading && (
        <div style={{ padding: "60px 56px", color: FT.navy60, fontSize: 14 }}>
          Loading…
        </div>
      )}
      {error && (
        <div
          style={{
            margin: "28px 56px",
            padding: "12px 16px",
            background: "#FBE6E6",
            border: "1px solid #F5C2C2",
            color: "#9B2C2C",
            borderRadius: 10,
            fontSize: 13
          }}
        >
          {error}
        </div>
      )}

      {!loading && !error && (
        <>
          <section style={{ padding: "28px 56px 0" }}>
            <ReferralStrip
              signupPageUrl={dashboard?.signupPageUrl}
              referralCode={dashboard?.referralCode}
            />
          </section>
          <section style={{ padding: "0 56px 0" }}>
            <div
              style={{
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "space-between",
                marginBottom: 24,
                gap: 20
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 12,
                    color: FT.navy60,
                    fontWeight: 700,
                    letterSpacing: 0.6,
                    textTransform: "uppercase",
                    display: "flex",
                    alignItems: "center",
                    gap: 8
                  }}
                >
                  <span
                    onClick={() => navigate(PARTNER_DASHBOARD)}
                    style={{ cursor: "pointer", color: FT.navy60 }}
                  >
                    Dashboard
                  </span>
                  <span style={{ color: FT.navy40 }}>›</span>
                  <span style={{ color: FT.navy }}>Commission</span>
                </div>
                <h1
                  style={{
                    fontFamily: "'DM Sans', system-ui, sans-serif",
                    fontSize: 34,
                    fontWeight: 800,
                    margin: "6px 0 0",
                    letterSpacing: -0.8,
                    color: FT.navy,
                    lineHeight: 1.15
                  }}
                >
                  Payout history
                </h1>
                <div style={{ fontSize: 13.5, color: FT.navy60, marginTop: 6 }}>
                  Commission is paid on the 20th of the month after each
                  invoice&apos;s repay date.
                  {showDistribution
                    ? " Each month shows the total FundTap pays your organisation, split by the amount due to each partner."
                    : " Click any payout to see the invoices it includes."}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {isLead && members.length > 0 && (
                  <select
                    value={partnerFilter ?? ""}
                    onChange={(e) =>
                      setPartnerFilter(
                        e.target.value ? Number(e.target.value) : null
                      )
                    }
                    style={{
                      padding: "8px 12px",
                      borderRadius: 8,
                      fontSize: 13,
                      fontWeight: 600,
                      background: "#fff",
                      color: FT.navy,
                      border: `1px solid ${FT.navy15}`,
                      cursor: "pointer",
                      fontFamily: "inherit"
                    }}
                  >
                    <option value="">All partners</option>
                    {members.map((m) => (
                      <option key={m.partnerId} value={m.partnerId}>
                        {m.partnerName}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: 14,
                marginBottom: 30
              }}
            >
              {[
                {
                  label: "Total paid",
                  value: fmt.money(totalPaid),
                  sub: `${paid.length} payout${paid.length === 1 ? "" : "s"}`
                },
                {
                  label: "Lifetime invoices",
                  value: String(lifetimeInvoices),
                  sub: "Across all payouts"
                },
                {
                  label: "Average payout",
                  value: fmt.money(averagePayout),
                  sub: "Per month"
                },
                {
                  label: "Upcoming",
                  value: fmt.money(expectedUpcoming),
                  sub: `${upcoming.length} payout${
                    upcoming.length === 1 ? "" : "s"
                  } estimated`
                }
              ].map((s) => (
                <div
                  key={s.label}
                  style={{
                    padding: "18px 20px",
                    background: "#fff",
                    border: `1px solid ${FT.navy08}`,
                    borderRadius: 14
                  }}
                >
                  <div
                    style={{
                      fontSize: 11.5,
                      color: FT.navy60,
                      fontWeight: 800,
                      letterSpacing: 0.6,
                      textTransform: "uppercase"
                    }}
                  >
                    {s.label}
                  </div>
                  <div
                    style={{
                      fontSize: 24,
                      fontWeight: 800,
                      marginTop: 6,
                      color: FT.navy,
                      letterSpacing: -0.6,
                      fontVariantNumeric: "tabular-nums"
                    }}
                  >
                    {s.value}
                  </div>
                  <div style={{ fontSize: 12, color: FT.navy60, marginTop: 4 }}>
                    {s.sub}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section style={{ padding: "0 56px 56px" }}>
            {upcoming.length > 0 && (
              <>
                <h2
                  style={{
                    fontFamily: "'DM Sans', system-ui, sans-serif",
                    fontSize: 16,
                    fontWeight: 800,
                    color: FT.navy,
                    letterSpacing: -0.2,
                    margin: "0 0 12px"
                  }}
                >
                  Upcoming
                </h2>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                    marginBottom: 28
                  }}
                >
                  {showDistribution
                    ? groupUpcomingByMonth(upcoming).map((g, i) => (
                        <MonthDistribution
                          key={g.id}
                          group={g}
                          kind="upcoming"
                          next={i === 0}
                        />
                      ))
                    : upcoming.map((p, i) => (
                        <PayoutRow
                          key={p.id}
                          payout={p}
                          kind={i === 0 ? "next" : "upcoming"}
                          isOpen={expanded === p.id}
                          onToggle={() =>
                            setExpanded(expanded === p.id ? null : p.id)
                          }
                          showPartner={isLead}
                        />
                      ))}
                </div>
              </>
            )}

            <h2
              style={{
                fontFamily: "'DM Sans', system-ui, sans-serif",
                fontSize: 16,
                fontWeight: 800,
                color: FT.navy,
                letterSpacing: -0.2,
                margin: "0 0 12px"
              }}
            >
              Paid
            </h2>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {showDistribution
                ? groupPaidByMonth(paid).map((g) => (
                    <MonthDistribution key={g.id} group={g} kind="paid" />
                  ))
                : paid.map((p) => (
                    <PayoutRow
                      key={p.id}
                      payout={p}
                      kind="paid"
                      isOpen={expanded === p.id}
                      onToggle={() =>
                        setExpanded(expanded === p.id ? null : p.id)
                      }
                      showPartner={isLead}
                    />
                  ))}
              {paid.length === 0 && (
                <div
                  style={{
                    padding: "32px 20px",
                    textAlign: "center",
                    color: FT.navy60,
                    fontSize: 13.5,
                    background: "#fff",
                    border: `1px solid ${FT.navy08}`,
                    borderRadius: 10
                  }}
                >
                  No payouts yet — once your clients start repaying, this is
                  where you&apos;ll see your earnings.
                </div>
              )}
            </div>
          </section>
        </>
      )}

      {addOpen && (
        <AddCustomerModal
          signupUrl={signupDisplayUrl}
          copied={false}
          onCopy={() => {}}
          onClose={() => setAddOpen(false)}
        />
      )}
    </div>
  );
}

function PayoutRow({ payout, kind, isOpen, onToggle, showPartner }) {
  const isNext = kind === "next";
  const isFuture = kind === "upcoming" || kind === "next";
  let iconBg = FT.mintBg;
  if (isNext) iconBg = "rgba(111,216,159,0.15)";
  else if (isFuture) iconBg = FT.paperWarm;
  let iconColor = FT.mintDeep;
  if (isNext) iconColor = FT.mint;
  else if (isFuture) iconColor = FT.navy60;
  const invoices = payout.invoices || [];
  // Org lead: attribute each paid payout (single partner) and each invoice line.
  const detailCols = showPartner
    ? "1.4fr 1fr 1fr 0.8fr 0.85fr 0.85fr"
    : "1.4fr 1fr 0.8fr 0.85fr 0.85fr";

  return (
    <div
      style={{
        border: `1px solid ${isNext ? "transparent" : FT.navy08}`,
        borderRadius: 14,
        background: isNext ? FT.navy : "#fff",
        color: isNext ? "#fff" : FT.navy,
        overflow: "hidden"
      }}
    >
      <button
        onClick={onToggle}
        style={{
          display: "grid",
          gridTemplateColumns: "40px 1fr auto",
          alignItems: "center",
          gap: 14,
          width: "100%",
          padding: "16px 20px",
          background: "transparent",
          border: "none",
          cursor: "pointer",
          fontFamily: "inherit",
          textAlign: "left",
          color: "inherit"
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 10,
            background: iconBg,
            color: iconColor,
            display: "grid",
            placeItems: "center"
          }}
        >
          {isFuture ? (
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="9" />
              <polyline points="12 7 12 12 15 14" />
            </svg>
          ) : (
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          )}
        </div>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span
              style={{ fontSize: 15, fontWeight: 800, letterSpacing: -0.2 }}
            >
              {payout.period}
            </span>
            {isNext && (
              <span
                style={{
                  fontSize: 10.5,
                  fontWeight: 800,
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                  color: FT.navy,
                  background: FT.mint,
                  padding: "2px 8px",
                  borderRadius: 99
                }}
              >
                Next
              </span>
            )}
            {isFuture && !isNext && (
              <span
                style={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: 0.4,
                  textTransform: "uppercase",
                  color: FT.navy60,
                  background: FT.navy04,
                  padding: "2px 8px",
                  borderRadius: 99
                }}
              >
                Estimated
              </span>
            )}
          </div>
          <div
            style={{
              fontSize: 12.5,
              color: isNext ? "rgba(255,255,255,0.65)" : FT.navy60,
              marginTop: 3
            }}
          >
            {isFuture
              ? `Pays ${fmt.date(payout.paidOn)}`
              : `Paid ${fmt.date(payout.paidOn)}`}{" "}
            · {payout.customerCount} client
            {payout.customerCount === 1 ? "" : "s"} · {payout.invoiceCount}{" "}
            invoice{payout.invoiceCount === 1 ? "" : "s"}
            {showPartner && payout.partnerName
              ? ` · ${payout.partnerName}`
              : ""}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ textAlign: "right" }}>
            <div
              style={{
                fontSize: 19,
                fontWeight: 800,
                fontVariantNumeric: "tabular-nums",
                letterSpacing: -0.4
              }}
            >
              {fmt.money(payout.amount)}
            </div>
            <div
              style={{
                fontSize: 11,
                color: isNext ? "rgba(255,255,255,0.55)" : FT.navy60,
                marginTop: 2
              }}
            >
              {isOpen ? "Hide invoices ▴" : "View invoices ▾"}
            </div>
          </div>
        </div>
      </button>

      {isOpen && (
        <div
          style={{
            borderTop: `1px solid ${isNext ? FT.mint : FT.navy08}`,
            background: isNext ? "#fff" : FT.navy04
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: detailCols,
              padding: "10px 20px",
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: 0.5,
              textTransform: "uppercase",
              color: FT.navy60,
              borderBottom: `1px solid ${FT.navy08}`
            }}
          >
            <div>Client</div>
            {showPartner && <div>Partner</div>}
            <div>Invoice</div>
            <div>Repay date</div>
            <div style={{ textAlign: "right" }}>Invoice $</div>
            <div style={{ textAlign: "right" }}>Commission</div>
          </div>
          {invoices.map((inv, idx) => (
            <div
              key={inv.quoteId ?? idx}
              style={{
                display: "grid",
                gridTemplateColumns: detailCols,
                padding: "12px 20px",
                fontSize: 13,
                alignItems: "center",
                borderBottom:
                  idx === invoices.length - 1
                    ? "none"
                    : `1px solid ${FT.navy08}`
              }}
            >
              <div style={{ fontWeight: 600, color: FT.navy }}>
                {inv.customerName}
              </div>
              {showPartner && (
                <div
                  style={{
                    fontSize: 12.5,
                    color: FT.navy60
                  }}
                >
                  {inv.partnerName || "—"}
                </div>
              )}
              <div
                style={{
                  fontVariantNumeric: "tabular-nums",
                  color: FT.navy80
                }}
              >
                {inv.quoteId ? `FT${inv.quoteId}` : "—"}
              </div>
              <div
                style={{
                  fontVariantNumeric: "tabular-nums",
                  color: FT.navy80
                }}
              >
                {fmt.dateShort(inv.repayDate)}
              </div>
              <div
                style={{
                  textAlign: "right",
                  fontVariantNumeric: "tabular-nums",
                  color: FT.navy80
                }}
              >
                {inv.fundingAmount != null
                  ? fmt.money0(inv.fundingAmount)
                  : "—"}
              </div>
              <div
                style={{
                  textAlign: "right",
                  fontVariantNumeric: "tabular-nums",
                  color: FT.mintDeep,
                  fontWeight: 700
                }}
              >
                {fmt.money(inv.commission)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

PayoutRow.propTypes = {
  payout: PropTypes.object,
  kind: PropTypes.string,
  isOpen: PropTypes.bool,
  onToggle: PropTypes.func,
  showPartner: PropTypes.bool
};

// Group paid payouts (each row is one partner for one month) by month, with a
// per-partner subtotal and a month grand total (the lump sum FundTap deposits).
function groupPaidByMonth(paid) {
  const map = new Map();
  for (const p of paid) {
    let g = map.get(p.period);
    if (!g) {
      g = {
        id: "PM-" + p.period,
        period: p.period,
        paidOn: p.paidOn,
        total: 0,
        partners: []
      };
      map.set(p.period, g);
    }
    g.total += p.amount || 0;
    g.partners.push({
      partnerName: p.partnerName || "—",
      amount: p.amount || 0,
      invoiceCount: p.invoiceCount || 0,
      customerCount: p.customerCount || 0,
      invoices: p.invoices || []
    });
  }
  const groups = Array.from(map.values());
  groups.forEach((g) => g.partners.sort((a, b) => b.amount - a.amount));
  groups.sort((a, b) => new Date(b.paidOn) - new Date(a.paidOn));
  return groups;
}

// Upcoming payouts arrive as one row per month with invoices tagged by partner;
// roll the invoices up into per-partner subtotals for the same distribution view.
function groupUpcomingByMonth(upcoming) {
  return upcoming
    .map((p) => {
      const byPartner = new Map();
      for (const inv of p.invoices || []) {
        const name = inv.partnerName || "—";
        let pr = byPartner.get(name);
        if (!pr) {
          pr = {
            partnerName: name,
            amount: 0,
            invoices: [],
            clients: new Set()
          };
          byPartner.set(name, pr);
        }
        pr.amount += inv.commission || 0;
        pr.invoices.push(inv);
        if (inv.customerName) pr.clients.add(inv.customerName);
      }
      const partners = Array.from(byPartner.values())
        .map((pr) => ({
          partnerName: pr.partnerName,
          amount: pr.amount,
          invoiceCount: pr.invoices.length,
          customerCount: pr.clients.size,
          invoices: pr.invoices
        }))
        .sort((a, b) => b.amount - a.amount);
      return {
        id: "UM-" + p.period,
        period: p.period,
        paidOn: p.paidOn,
        total: p.amount || 0,
        partners
      };
    })
    .sort((a, b) => new Date(a.paidOn) - new Date(b.paidOn));
}

// One month card for the head contact: grand total to distribute + a row per
// sub-partner with their subtotal, each expandable to the underlying invoices.
function MonthDistribution({ group, kind, next }) {
  const isFuture = kind === "upcoming";
  const [openPartner, setOpenPartner] = useState(null);
  return (
    <div
      style={{
        border: next ? `1.5px solid ${FT.mint}` : `1px solid ${FT.navy08}`,
        borderRadius: 14,
        background: "#fff",
        overflow: "hidden",
        marginBottom: 12
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "16px 20px",
          background: next ? FT.navy : FT.navy04,
          borderBottom: next ? "none" : `1px solid ${FT.navy08}`
        }}
      >
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span
              style={{
                fontSize: 15,
                fontWeight: 800,
                color: next ? "#fff" : FT.navy
              }}
            >
              {group.period}
            </span>
            {next && (
              <span
                style={{
                  fontSize: 10.5,
                  fontWeight: 800,
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                  color: FT.navy,
                  background: FT.mint,
                  padding: "2px 8px",
                  borderRadius: 99
                }}
              >
                Next
              </span>
            )}
          </div>
          <div
            style={{
              fontSize: 12.5,
              color: next ? "rgba(255,255,255,0.7)" : FT.navy60,
              marginTop: 2
            }}
          >
            {isFuture
              ? `Expected ${fmt.date(group.paidOn)}`
              : `Paid ${fmt.date(group.paidOn)}`}{" "}
            · {group.partners.length} partner
            {group.partners.length === 1 ? "" : "s"}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div
            style={{
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: 0.5,
              textTransform: "uppercase",
              color: next ? "rgba(255,255,255,0.6)" : FT.navy60
            }}
          >
            {isFuture ? "Total expected" : "Total to distribute"}
          </div>
          <div
            style={{
              fontSize: 20,
              fontWeight: 800,
              color: next ? "#fff" : FT.navy,
              fontVariantNumeric: "tabular-nums"
            }}
          >
            {fmt.money(group.total)}
          </div>
        </div>
      </div>

      {group.partners.map((pr, idx) => {
        const open = openPartner === pr.partnerName;
        return (
          <div
            key={pr.partnerName}
            style={{
              borderBottom:
                idx === group.partners.length - 1
                  ? "none"
                  : `1px solid ${FT.navy08}`
            }}
          >
            <button
              onClick={() => setOpenPartner(open ? null : pr.partnerName)}
              style={{
                display: "grid",
                gridTemplateColumns: "1.6fr 0.9fr 0.9fr auto",
                alignItems: "center",
                gap: 12,
                width: "100%",
                padding: "12px 20px",
                background: open ? FT.mintBg : "transparent",
                border: "none",
                cursor: "pointer",
                fontFamily: "inherit",
                textAlign: "left",
                color: FT.navy
              }}
            >
              <div style={{ fontWeight: 700, fontSize: 14 }}>
                {pr.partnerName}
              </div>
              <div style={{ fontSize: 12.5, color: FT.navy60 }}>
                {pr.customerCount} client{pr.customerCount === 1 ? "" : "s"}
              </div>
              <div style={{ fontSize: 12.5, color: FT.navy60 }}>
                {pr.invoiceCount} invoice{pr.invoiceCount === 1 ? "" : "s"}
              </div>
              <div
                style={{
                  textAlign: "right",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  justifyContent: "flex-end"
                }}
              >
                <span
                  style={{
                    fontSize: 15,
                    fontWeight: 800,
                    fontVariantNumeric: "tabular-nums"
                  }}
                >
                  {fmt.money(pr.amount)}
                </span>
                <span style={{ fontSize: 11, color: FT.navy60 }}>
                  {open ? "▴" : "▾"}
                </span>
              </div>
            </button>

            {open && (
              <div
                style={{
                  background: FT.navy04,
                  borderTop: `1px solid ${FT.navy08}`
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1.4fr 1fr 0.8fr 0.85fr 0.85fr",
                    padding: "8px 20px",
                    fontSize: 10.5,
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    textTransform: "uppercase",
                    color: FT.navy60,
                    borderBottom: `1px solid ${FT.navy08}`
                  }}
                >
                  <div>Client</div>
                  <div>Invoice</div>
                  <div>Repay date</div>
                  <div style={{ textAlign: "right" }}>Invoice $</div>
                  <div style={{ textAlign: "right" }}>Commission</div>
                </div>
                {pr.invoices.map((inv, i2) => (
                  <div
                    key={inv.quoteId ?? i2}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1.4fr 1fr 0.8fr 0.85fr 0.85fr",
                      padding: "10px 20px",
                      fontSize: 13,
                      alignItems: "center",
                      borderBottom:
                        i2 === pr.invoices.length - 1
                          ? "none"
                          : `1px solid ${FT.navy08}`
                    }}
                  >
                    <div style={{ fontWeight: 600, color: FT.navy }}>
                      {inv.customerName}
                    </div>
                    <div
                      style={{
                        color: FT.navy80,
                        fontVariantNumeric: "tabular-nums"
                      }}
                    >
                      {inv.quoteId ? `FT${inv.quoteId}` : "—"}
                    </div>
                    <div
                      style={{
                        color: FT.navy80,
                        fontVariantNumeric: "tabular-nums"
                      }}
                    >
                      {fmt.dateShort(inv.repayDate)}
                    </div>
                    <div
                      style={{
                        textAlign: "right",
                        color: FT.navy80,
                        fontVariantNumeric: "tabular-nums"
                      }}
                    >
                      {inv.fundingAmount != null
                        ? fmt.money0(inv.fundingAmount)
                        : "—"}
                    </div>
                    <div
                      style={{
                        textAlign: "right",
                        color: FT.mintDeep,
                        fontWeight: 700,
                        fontVariantNumeric: "tabular-nums"
                      }}
                    >
                      {fmt.money(inv.commission)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

MonthDistribution.propTypes = {
  group: PropTypes.object,
  kind: PropTypes.string,
  next: PropTypes.bool
};
